import React, { useCallback, useMemo } from 'react';
import { FlatList, StyleSheet, ListRenderItem } from 'react-native';
import { ConversationCard } from './ConversationCard';
import { IConversation } from '@/types/admin/messages';
import { PERFORMANCE_CONSTANTS, FLATLIST_PROPS } from '@/constants/admin/performance';
import { generateConversationKey, validateUniqueKeys } from '@/utils/admin/keyUtils';

interface ConversationListProps {
  conversations: IConversation[];
  hasNextPage: boolean;
  isLoading: boolean;
  loadMore: () => void;
  selectedConversation?: number;
  onConversationSelect: (conversationId: number) => void;
}

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  hasNextPage,
  isLoading,
  loadMore,
  selectedConversation,
  onConversationSelect,
}) => {

  // Validate unique keys in development
  React.useEffect(() => {
    validateUniqueKeys(conversations, (conv) => conv.id, 'conversations');
  }, [conversations]);

  // Memoized handlers for performance
  const handleConversationPress = useCallback((conversationId: number) => {
    onConversationSelect(conversationId);
  }, [onConversationSelect]);

  const keyExtractor = useCallback((item: IConversation) => generateConversationKey(item.id), []);

  const renderConversationItem: ListRenderItem<IConversation> = useCallback(({ item }) => (
    <ConversationCard
      conversation={item}
      onPress={() => handleConversationPress(item.id)}
      isSelected={selectedConversation === item.id}
    />
  ), [handleConversationPress, selectedConversation]);

  // Memoized getItemLayout for better performance
  const getItemLayout = useMemo(
    () => FLATLIST_PROPS.getItemLayout(PERFORMANCE_CONSTANTS.CONVERSATION_ITEM_HEIGHT),
    []
  );

  // Handle end reached for infinite scroll
  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isLoading) {
      loadMore();
    }
  }, [hasNextPage, isLoading, loadMore]);

  return (
    <FlatList
      data={conversations}
      renderItem={renderConversationItem}
      keyExtractor={keyExtractor}
      getItemLayout={getItemLayout}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={styles.listContent}
      style={styles.container}
      // Performance optimizations
      removeClippedSubviews={FLATLIST_PROPS.removeClippedSubviews}
      maxToRenderPerBatch={FLATLIST_PROPS.maxToRenderPerBatch}
      windowSize={FLATLIST_PROPS.windowSize}
      initialNumToRender={FLATLIST_PROPS.initialNumToRender}
      updateCellsBatchingPeriod={FLATLIST_PROPS.updateCellsBatchingPeriod}
      // Infinite scroll
      onEndReached={handleEndReached}
      onEndReachedThreshold={0.5}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  listContent: {
    paddingBottom: 16,
  },
});
